import MiniCADAPI from "./MiniCADAPI";
import azure from "azure-storage";
import {
  GLOBAL_PROJECT_TYPE_CHAMBER,
  GLOBAL_PROJECT_TYPE_PIPE
} from "./types";
import produce, { immerable } from "immer";

/**
 * Project data access layer class.
 */
export default class Project{
  static PROJECT_TYPE = {
    chamber: GLOBAL_PROJECT_TYPE_CHAMBER,
    pipe: GLOBAL_PROJECT_TYPE_PIPE
  };
  /**
   * The processing status enumerators
   * @type {{copied: string, success: string, pending: string, processing: string, failed: string, unsubmitted: string}}
   */
  static PROCESSING_STATUS = {
    unsubmitted: "unsubmitted",
    pending: "pending",
    processing: "processing",
    failed: "failed",
    failedDownload: "failed",
    success: "success",
    copied: "copied" //WARN: This is local status only, do not upload to the database
  };
  [immerable] = true;

  /**
   * Project constructor
   * @param id {string | null}
   * @param projectName {String}
   * @param engineer {String}
   * @param city {String}
   * @param state {String}
   * @param country {String}
   * @param region {String}
   * @param language {String}
   * @param createdDate {Date}
   * @param statusDate {Date}
   * @param editedDate {Date}
   * @param processingStatus {Project.PROCESSING_STATUS}
   * @param bomFileUrl {String}
   * @param active {Boolean}
   * @param workItemId {String}
   * @param inputFileURL {String}
   * @param outputFileURL {String}
   * @param projectType {String}
   * @param outputFileURLPipe
   * @param bomFileUrlPipe
   * @param isMultiBedProject
   * @param generateHydroCAD
   */
  constructor(
    id = null,
    projectName = null,
    engineer = null,
    city = null,
    state = null,
    country = null,
    region = null,
    language = null,
    createdDate = new Date(),
    statusDate = new Date(),
    editedDate = null,
    processingStatus = Project.PROCESSING_STATUS.unsubmitted,
    bomFileUrl = null,
    active = true,
    workItemId = "",
    inputFileURL = "",
    outputFileURL = "",
    projectType = null,
    outputFileURLPipe = "",
    bomFileUrlPipe = "",
    isMultiBedProject = false,
    generateHydroCAD = false
  ) {
    /* class properties */
    this.id = id;
    this.projectName = projectName;
    this.engineer = engineer;
    this.city = city;
    this.state = state;
    this.country = country;
    this.region = region;
    this.language = language;
    this.createdDate = createdDate;
    this.statusDate = statusDate;
    this.editedDate = editedDate;
    this.processingStatus = processingStatus;
    this.bomFileUrl = bomFileUrl;
    this.active = active;
    this.workItemId = workItemId;
    this.inputFileURL = inputFileURL;
    this.outputFileURL = outputFileURL;
    this.projectType = projectType;
    this.outputFileURLPipe = outputFileURLPipe;
    this.bomFileUrlPipe = bomFileUrlPipe;
    this.isMultiBedProject = isMultiBedProject;
    this.generateHydroCAD = generateHydroCAD;
  }

  /**
   * Project Object de-serializer
   * @param {Object} record The object form Azure storage table.
   * @returns {Project}
   * @private
   */
  static _deserializeFromEntity( record ) {
    return new Project(
      record.RowKey._,
      record.projectName._,
      record.engineer._,
      record.city._,
      record.state._,
      record.country ? record.country._ : "",
      record.region ? record.region._ : "",
      record.language ? record.language._ : "",
      record.createdDate._,
      record.statusDate._,
      ( record.editedDate ) ? record.editedDate._ : null,
      record.processingStatus._,
      ( record.bomFileUrl ) ? record.bomFileUrl._ : "",
      record.active._,
      ( record.workItemId ) ? record.workItemId._ : "",
      ( record.inputFileURL ) ? record.inputFileURL._ : "",
      ( record.outputFileURL ) ? record.outputFileURL._ : "",
      record.projectType._,
      ( record.outputFileURLPipe ) ? record.outputFileURLPipe._ : "",
      ( record.bomFileUrlPipe ) ? record.bomFileUrlPipe._ : "",
      record.isMultiBedProject ? record.isMultiBedProject._ : false,
      record.generateHydroCAD ? record.generateHydroCAD._ : false
    );
  }

  /**
   * Project Object serializer
   * @param project {Project} - The object form Azure storage table.
   * @param oid {String}
   * @returns {*}
   * @private
   */
  static _serializeToEntity( project, oid ) {
    const entGen = azure.TableUtilities.entityGenerator;
    return {
      PartitionKey: entGen.String(oid),
      RowKey: entGen.String(project.id),

      projectName: entGen.String(project.projectName),
      engineer: entGen.String(project.engineer),
      city: entGen.String(project.city),
      state: entGen.String(project.state),
      country: entGen.String(project.country),
      region: entGen.String(project.region),
      language: entGen.String(project.language),
      createdDate: entGen.DateTime(project.createdDate),
      statusDate: entGen.DateTime(project.statusDate),
      editedDate: entGen.DateTime(project.editedDate),
      processingStatus: entGen.String(project.processingStatus),
      bomFileUrl: entGen.String(project.bomFileUrl),
      bomFileUrlPipe: entGen.String(project.bomFileUrlPipe),
      active: entGen.Boolean(project.active),
      workItemId: entGen.String(project.workItemId),
      inputFileURL: entGen.String(project.inputFileURL),
      outputFileURL: entGen.String(project.outputFileURL),
      outputFileURLPipe: entGen.String(project.outputFileURLPipe),
      projectType: entGen.String(project.projectType),
      isMultiBedProject: entGen.Boolean(project.isMultiBedProject),
      generateHydroCAD: entGen.Boolean(project.generateHydroCAD)
    };
  }

  /**
   * This function fetches project records from backend table storage.
   * @param oid {String} - Unique key used in table storage, this is the user's iod.
   * @param tableToken {String} - The SAS access token in order to access the table.
   * @returns {Array<Project>}
   */
  static async fetchProjects( oid, tableToken ) {
    let records = await MiniCADAPI.getProjectRecords(oid, tableToken);
    return records.map(Project._deserializeFromEntity);
  }

  /**
   * This function updates project records in table storage.
   * @param oid {String} - Unique key used in table storage, this is the user's iod.
   * @param tableToken {String} - The SAS access token in order to access the table.
   * @returns {Promise<void>}
   */
  async persist( oid, tableToken ) {
    if (!this.country) {
      console.log("p country missing (CA or MX)", this.region);
      //correcting Canada
      if (this.region === "Canada") {
        this.country = this.region;
        console.log("c:", this.country);
      }
      if (this.region === "México") {
        this.country = this.region;
        console.log("m:", this.country);
      }
    }
    if (!this.region) console.error("p region missing");

    if (!this.city) console.error("p city missing");

    if (!this.language) console.error("p language missing");

    if (this.region !== "México" && this.region !== "Canada" && this.region !== "USA" ) {
      this.state = this.country;
      this.country = this.region;
      console.log("fix state and country for non US,CA,MX:", this.country, this.state);
    }

    if (this._invalid()) {
      const thisMsg = JSON.stringify(this, null, 2);
      const msg = `Some required fields are not present, please check object consistency before saving: ${thisMsg}`;
      throw new Error(msg);
    }
    this.editedDate = new Date();
    let entity = Project._serializeToEntity(this, oid);
    return await MiniCADAPI.putProjectRecord(entity, tableToken);
  }

  _invalid() {
    if (this["region"] === "USA" || this["region"] === "Canada"|| this["region"] === "México") {
      return [
        "id",
        "projectName",
        "engineer",
        "city",
        "state",
        "projectType",
        "region",
        "language"
      ].some(( prop ) => {
        return !this[prop];
      });
    } else {
      return [
        "id",
        "projectName",
        "engineer",
        "city",
        "country",
        "projectType",
        "region",
        "language"
      ].some(( prop ) => {
        return !this[prop];
      });
    }
  }

  /**
   * This function fetches *this* project record from backend table storage.
   * @param oid {String} - Unique key used in table storage, this is the user's iod.
   * @param tableToken {String} - The SAS access token in order to access the table.
   * @returns {Promise<Project>}
   */
  async fetch( oid, tableToken ) {
    let entity = await MiniCADAPI
      .getProjectRecord(oid, ( this.id ).toString(), tableToken);

    if (!entity)
      throw new Error(`Project with id = '${this.id}' and partition key ='${oid}' not found.`);

    let project = Project
      ._deserializeFromEntity(entity);

    Object.assign(this, project);

    if(project.processingStatus === 'failedDownload') {
      project.processingStatus = "failed";
    }
    return project;
  }

  /**
   * This function fetch project state from blob storage.
   * @param oid {String} - Unique key used in blob storage, this is the user's iod.
   * @param blobToken {String} - The SAS access token in order to access the blob.
   * @returns {Promise<void>}
   */
  async fetchState( oid, blobToken ) {
    return await MiniCADAPI
      .getProjectFile(
        oid,
        `${this.id}-state`,
        blobToken
        // progressFn
      );
  }

  /**
   * This function persist project state to blob storage.
   * @param state {Object} - All data required to return the project to last
   *                  saved state.
   * @param oid {String} - Unique key used in blob storage, this is the user's iod.
   * @param blobToken {String} - The SAS access token in order to access the blob.
   * @param progressFN {Function} - The progress function to report back upload progress.
   * @returns {Promise<void>}
   */
  async persistState( state, oid, blobToken, progressFN ) {
    await MiniCADAPI
      .putProjectFile(
        oid,
        `${this.id}-state`,
        state,
        blobToken,
        progressFN
      );
  }

  /**
   * This function persist project state to blob storage.
   * @param state {Object} - All data required to return the project to last saved state.
   * @param oid {String} - Unique key used in blob storage, this is the user's iod.
   * @param blobToken {String} - The SAS access token in order to access the blob.
   * @param progressFN {Function} - Progress function to report back upload progress.
   * @returns {Promise<void>}
   */
  async persistAutomationState( state, oid, blobToken, progressFN ) {
    await MiniCADAPI
      .putProjectFile(
        oid,
        `${this.id}-automation`,
        state,
        blobToken,
        progressFN
      );
  }

  /**
   * This function submit project automation task to forge via Azure.
   * @param oid {String} - Unique key used in blob storage, this is the user's iod.
   * @param idToken {String} - Token id.
   * @param email {String}
   * @param accessToken {String}
   * @param hydroCADRequest
   * @returns {Promise<void>}
   */
  async submitAutomationTask( oid, idToken, email, accessToken, hydroCADRequest = false ) {
    return await MiniCADAPI
      .postAutomationStart(
        idToken,
        email,
        accessToken,
        oid,
        this.id,
        hydroCADRequest
      );
  }

  /**
   * This function submit project automation task to forge via Azure.
   * @param oid {String} - Unique key used in blob storage, this is the user's iod.
   * @returns {Promise<string>}
   */
  async checkWorkItemStatus( oid ) {
    return await MiniCADAPI
      .getTaskStatus(
        this.workItemId,
        oid,
        this.id,
        this.isMultiBedProject
      );
  }
}



// WEBPACK FOOTER //
// ./src/lib/project.js